/**
 * Carrossel de vitrine do perfil: o card do meio em destaque e os vizinhos
 * recuados, avancando sozinho.
 *
 * Usa rolagem nativa com scroll-snap em vez de um trilho com transform: o dedo
 * arrasta de verdade, a inercia e a do sistema e o teclado percorre os cards
 * sem codigo proprio. O avanco automatico so empurra a rolagem.
 *
 * Quem interage assume o controle: toque, arraste, clique nas setas ou foco
 * param o avanco de vez (o hover apenas pausa). Quem pede menos movimento no
 * sistema nunca recebe avanco automatico.
 */

import React from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const DEFAULT_INTERVAL_MS = 4000;

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * Centraliza um card no trilho. Usa a diferenca entre os retangulos em vez de
 * `offsetLeft`, que e relativo ao ancestral posicionado — nem sempre o trilho.
 */
function centerCard(track: HTMLElement | null, card: HTMLElement | null): void {
  if (!track || !card || typeof track.scrollBy !== "function") return;
  const trackRect = track.getBoundingClientRect();
  const cardRect = card.getBoundingClientRect();
  if (trackRect.width === 0) return;

  const delta =
    cardRect.left + cardRect.width / 2 - (trackRect.left + trackRect.width / 2);
  if (Math.abs(delta) < 1) return;
  track.scrollBy({ left: delta, behavior: "smooth" });
}

interface PreviewCarouselProps<T> {
  items: T[];
  getKey: (item: T, index: number) => string;
  renderItem: (item: T, isActive: boolean) => React.ReactNode;
  ariaLabel: string;
  previousLabel: string;
  nextLabel: string;
  intervalMs?: number;
  testId?: string;
  /** Largura do card. Chip de horario precisa de bem menos que card de servico. */
  itemWidthClass?: string;
  /** Destaque de escala no card ativo: faz sentido em imagem, nao em chip. */
  emphasizeActive?: boolean;
}

export function PreviewCarousel<T>({
  items,
  getKey,
  renderItem,
  ariaLabel,
  previousLabel,
  nextLabel,
  intervalMs = DEFAULT_INTERVAL_MS,
  testId,
  itemWidthClass = "w-[72%] sm:w-[46%]",
  emphasizeActive = true,
}: PreviewCarouselProps<T>) {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [autoPlay, setAutoPlay] = React.useState(true);
  const [hovering, setHovering] = React.useState(false);
  const [pageVisible, setPageVisible] = React.useState(true);
  const trackRef = React.useRef<HTMLUListElement | null>(null);
  const cardRefs = React.useRef<Array<HTMLLIElement | null>>([]);

  const total = items.length;
  const canRotate = total > 1;

  React.useEffect(() => {
    setActiveIndex((current) => (current < total ? current : 0));
  }, [total]);

  const goTo = React.useCallback(
    (nextIndex: number) => {
      if (total === 0) return;
      const normalized = ((nextIndex % total) + total) % total;
      setActiveIndex(normalized);

      centerCard(trackRef.current, cardRefs.current[normalized]);
    },
    [total]
  );

  /** Interacao explicita: quem assumiu o controle nao o perde de volta. */
  const stopAutoPlay = React.useCallback(() => setAutoPlay(false), []);

  /**
   * Aba em segundo plano nao recebe quadros de animacao: o indice avancaria e
   * a rolagem ficaria para tras, deixando o destaque num card fora da vista.
   * Alem de nao animar o que ninguem esta olhando.
   */
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const sync = () => setPageVisible(document.visibilityState !== "hidden");
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, []);

  React.useEffect(() => {
    if (!canRotate || !autoPlay || hovering || !pageVisible) return;
    if (prefersReducedMotion()) return;

    const timer = window.setInterval(() => {
      setActiveIndex((current) => {
        const next = (current + 1) % total;
        centerCard(trackRef.current, cardRefs.current[next]);
        return next;
      });
    }, intervalMs);

    return () => window.clearInterval(timer);
  }, [autoPlay, canRotate, hovering, intervalMs, pageVisible, total]);

  /**
   * Arrastar com o dedo nao passava pelo estado: o destaque ficava no card
   * anterior e as setas partiam do lugar errado. A rolagem manual passa a
   * eleger o card mais proximo do centro.
   */
  React.useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let frame = 0;
    const syncActiveFromScroll = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const trackRect = track.getBoundingClientRect();
        if (trackRect.width === 0) return;
        const center = trackRect.left + trackRect.width / 2;

        let closest = 0;
        let smallest = Number.POSITIVE_INFINITY;
        cardRefs.current.forEach((card, index) => {
          if (!card) return;
          const rect = card.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - center);
          if (distance < smallest) {
            smallest = distance;
            closest = index;
          }
        });
        setActiveIndex(closest);
      });
    };

    track.addEventListener("scroll", syncActiveFromScroll, { passive: true });
    return () => {
      window.cancelAnimationFrame(frame);
      track.removeEventListener("scroll", syncActiveFromScroll);
    };
  }, [total]);

  if (total === 0) return null;

  return (
    <div
      className="relative"
      data-testid={testId}
      data-active-index={activeIndex}
      data-autoplay={autoPlay ? "on" : "off"}
      onPointerDown={stopAutoPlay}
      onTouchStart={stopAutoPlay}
      onFocus={stopAutoPlay}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <ul
        ref={trackRef}
        aria-label={ariaLabel}
        className={`flex snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain scroll-smooth py-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
          emphasizeActive ? "px-[12%] sm:px-[22%]" : "px-1"
        }`}
      >
        {items.map((item, index) => {
          const isActive = index === activeIndex;
          return (
            <li
              key={getKey(item, index)}
              ref={(element) => {
                cardRefs.current[index] = element;
              }}
              data-active={isActive ? "true" : "false"}
              className={`shrink-0 snap-center transition-all duration-300 ease-out motion-reduce:transition-none ${itemWidthClass} ${
                emphasizeActive && !isActive
                  ? "scale-90 opacity-60 hover:opacity-90"
                  : "scale-100 opacity-100"
              }`}
            >
              {renderItem(item, isActive)}
            </li>
          );
        })}
      </ul>

      {canRotate && (
        <div className="mt-1 flex items-center justify-center gap-2">
          <CarouselControl
            label={previousLabel}
            onClick={() => {
              stopAutoPlay();
              goTo(activeIndex - 1);
            }}
          >
            <FiChevronLeft className="h-5 w-5" aria-hidden="true" />
          </CarouselControl>

          <p className="text-xs font-semibold tabular-nums text-slate-500">
            {activeIndex + 1}/{total}
          </p>

          <CarouselControl
            label={nextLabel}
            onClick={() => {
              stopAutoPlay();
              goTo(activeIndex + 1);
            }}
          >
            <FiChevronRight className="h-5 w-5" aria-hidden="true" />
          </CarouselControl>
        </div>
      )}
    </div>
  );
}

function CarouselControl({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-primary-200 hover:text-primary-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
    >
      {children}
    </button>
  );
}
